/**
 * Creates a preview table from the CSV data
 * @param {Array} rows - Array of rows, each row is an array of cell values
 * @param {Function} getMappedColumnsFn - Function to get mapped columns
 * @returns {HTMLElement} - Table wrapper div element
 */
export function createPreviewTable(rows, getMappedColumnsFn) {
  if (!rows || rows.length === 0) {
    return document.createElement('div');
  }
  
  const wrapperDiv = document.createElement('div');
  wrapperDiv.className = 'table-responsive';
  const table = document.createElement('table');
  table.className = 'table table-bordered border';
  table.appendChild(createTableHeader(rows[0], getMappedColumnsFn));
  table.appendChild(createTableBody(rows.slice(1), rows[0], getMappedColumnsFn));
  wrapperDiv.appendChild(table);
  
  return wrapperDiv;
}

/**
 * Creates the table header
 * @param {Array} firstRow - First row of data (headers)
 * @param {Function} getMappedColumnsFn - Function to get mapped columns
 * @returns {HTMLElement} - Table header element
 */
function createTableHeader(firstRow, getMappedColumnsFn) {
  const thead = document.createElement('thead');
  const headerRow = document.createElement('tr');
  
  const mappedColumns = getMappedColumnsFn(firstRow);
  
  if (Array.isArray(mappedColumns)) {
    mappedColumns.forEach(({ destinationHeader }) => {
      const th = document.createElement('th');
      th.className = 'px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider';
      th.textContent = destinationHeader;
      headerRow.appendChild(th);
    });
  }
  
  thead.appendChild(headerRow);
  return thead;
}

/**
 * Creates the table body
 * @param {Array} dataRows - Array of data rows
 * @param {Array} firstRow - First row of data (headers)
 * @param {Function} getMappedColumnsFn - Function to get mapped columns
 * @returns {HTMLElement} - Table body element
 */
function createTableBody(dataRows, firstRow, getMappedColumnsFn) {
  const tbody = document.createElement('tbody');
  tbody.className = 'bg-white divide-y divide-gray-200';
  
  // The getMappedColumnsFn function is passed in from the controller
  // and already has access to the mappedHeaders through binding
  const mappedColumns = getMappedColumnsFn(firstRow);
  
  // Check if mappedColumns is an array before using forEach
  if (Array.isArray(mappedColumns)) {
    dataRows.forEach(row => {
      const tr = document.createElement('tr');
      
      mappedColumns.forEach(({ sourceIndex }) => {
        const td = document.createElement('td');
        td.className = 'px-6 py-4 whitespace-nowrap text-sm text-gray-500';
        td.textContent = row[sourceIndex] ?? '';
        tr.appendChild(td);
      });
      
      tbody.appendChild(tr);
    });
  }
  
  return tbody;
}

/**
 * Gets the mapped columns from headers mapping
 * @param {Array} firstRow - First row of data (headers)
 * @param {Object} mappedHeaders - Object mapping source indices to destination headers
 * @returns {Array} - Array of mapped column objects
 */
export function getMappedColumns(firstRow, mappedHeaders) {
  const mappedColumns = [];
  
  Object.entries(mappedHeaders).forEach(([sourceIndex, destinationHeader]) => {
    if (destinationHeader) {
      mappedColumns.push({
        sourceIndex: parseInt(sourceIndex),
        destinationHeader: destinationHeader,
        sourceHeader: firstRow[parseInt(sourceIndex)]
      });
    }
  });
  
  return mappedColumns.sort((a, b) => a.destinationHeader.localeCompare(b.destinationHeader));
}
